import { FormEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ChatScope, Citation, Conversation, TimelineEvent, Video } from "../lib/api";
import { ChatMode, ChatTurn, useChatStore } from "../lib/chatStore";
import { useAuth } from "../lib/auth";
import { NoteDraft } from "./NotesView";
import TimeEntryModal from "./TimeEntryModal";

// Same convention as VideoPlayer: play_url / raw paths are returned as
// "/api/..."; rebase onto the configured API base so a bare <video>/<img>
// src works in dev (absolute VITE_API_BASE) and prod (same-origin "/api").
const MEDIA_BASE = (import.meta as any).env?.VITE_API_BASE ?? "/api";

function mediaSrc(apiPath: string): string {
  return `${MEDIA_BASE}${apiPath.replace(/^\/api/, "")}`;
}

/** Format seconds as M:SS / H:MM:SS for clip citations. */
function formatClock(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds)) return "0:00";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}

interface Props {
  caseId: number;
  readyCount: number;
  /** True when this pane is the visible tab (auto-scroll + focus on activation). */
  active?: boolean;
  onTimelineSaved?: () => void;
  /** Map of `chat:<query_id>` -> existing note id, so each turn can show
   *  whether its Q&A has already been captured. */
  savedNotesIndex?: Map<string, number>;
  /** Persist a note immediately and return its id. */
  onSaveNote?: (draft: NoteDraft) => Promise<number>;
  /** Jump to the Notes tab and open the given note. */
  onOpenNote?: (noteId: number) => void;
  /** Hand a pre-filled NoteDraft to the Notes tab (highlight-to-capture). */
  onCaptureSelection?: (draft: NoteDraft) => void;
}

export default function ChatPane({
  caseId,
  readyCount,
  active = true,
  onTimelineSaved,
  savedNotesIndex,
  onSaveNote,
  onOpenNote,
  onCaptureSelection,
}: Props) {
  const chat = useChatStore((s) => s.byCase[caseId]);
  const ensure = useChatStore((s) => s.ensure);
  const ask = useChatStore((s) => s.ask);
  const setDraft = useChatStore((s) => s.setDraft);
  const setMode = useChatStore((s) => s.setMode);
  const startPolling = useChatStore((s) => s.startPolling);
  const stopPolling = useChatStore((s) => s.stopPolling);
  const loadConversations = useChatStore((s) => s.loadConversations);
  const setSearch = useChatStore((s) => s.setSearch);
  const setScope = useChatStore((s) => s.setScope);
  const newChat = useChatStore((s) => s.newChat);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const renameConversation = useChatStore((s) => s.renameConversation);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const setShared = useChatStore((s) => s.setShared);
  const [mobilePane, setMobilePane] = useState<"sidebar" | "thread">("thread");
  // "Quick log time" trigger lives in the sidebar's eyebrow row so users
  // who routinely log billable time can open the modal without hunting
  // through the profile menu. Gated to roles that can actually create
  // entries (mirrors the gate in AppShell).
  const authUser = useAuth((s) => s.user);
  const canLogTime = authUser?.role === "lawyer" || authUser?.role === "assistant";
  const [timeEntryOpen, setTimeEntryOpen] = useState(false);

  // Make sure there's a state slot for this case before reading anything.
  useEffect(() => { ensure(caseId); }, [caseId, ensure]);

  // First time we see this case in this session, load the visible tab.
  useEffect(() => {
    if (!chat) return;
    if (!chat.conversationsLoadedByScope[chat.scope]) {
      void loadConversations(caseId, undefined, chat.scope);
    }
  }, [caseId, chat?.scope, chat?.conversationsLoadedByScope, loadConversations]);

  const visibleConversations = chat?.conversationsByScope[chat.scope] ?? [];
  const visibleLoaded = chat?.conversationsLoadedByScope[chat.scope] ?? false;

  // If the user lands on a case with existing conversations and hasn't
  // picked one, drop them into the most recent one (preserves the previous
  // single-thread feel for users who never click "New chat"). We
  // explicitly skip this when the user just clicked "New chat" so we don't
  // immediately undo their action and snap back to the previous thread.
  useEffect(() => {
    if (!chat || !visibleLoaded) return;
    if (chat.activeId != null) return;
    if (chat.intentNewChat) return;
    if (chat.scope !== "mine") return; // only auto-open from the user's own tab
    const newest = visibleConversations[0];
    if (newest && !chat.newDraft.trim()) {
      void selectConversation(caseId, newest.id);
    }
  }, [
    caseId,
    chat?.scope,
    visibleLoaded,
    chat?.activeId,
    visibleConversations,
    chat?.newDraft,
    chat?.intentNewChat,
    selectConversation,
  ]);

  // Reference-count the poller so it stays alive while the chat is mounted.
  useEffect(() => {
    startPolling(caseId);
    return () => stopPolling(caseId);
  }, [caseId, startPolling, stopPolling]);

  if (!chat) return null;

  const activeThread = chat.activeId != null ? chat.threads[chat.activeId] : null;
  // Find the active conversation across whichever bucket holds it. The user
  // can be reading a Shared chat while the sidebar tab is "Mine" — we
  // still need its metadata for the read-only banner / composer state.
  const activeConv = chat.activeId == null
    ? null
    : findConversation(chat.conversationsByScope, chat.activeId);
  const isReadOnly = !!activeConv && !activeConv.is_owner;
  const activeTurns = activeThread?.turns ?? [];
  const activeHydrated = chat.activeId == null ? true : Boolean(activeThread?.hydrated);

  function exportActiveChatPdf() {
    if (!activeHydrated || activeTurns.length === 0) return;
    const conversationTitle = activeConv?.title?.trim()
      || activeConv?.last_question?.trim()
      || "Chat transcript";
    const popup = window.open("", "_blank");
    if (!popup) return;
    popup.document.open();
    popup.document.write(buildChatExportHtml({
      caseId,
      title: conversationTitle,
      ownerName: activeConv?.owner_display_name ?? null,
      turns: activeTurns,
      exportedAt: new Date(),
    }));
    popup.document.close();
    popup.focus();
    popup.setTimeout(() => {
      popup.print();
    }, 250);
  }

  return (
    <div className="chat-workspace">
      <div className="chat-mobile-tabs" role="tablist" aria-label="Chat sections">
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === "thread"}
          className={mobilePane === "thread" ? "active" : ""}
          onClick={() => setMobilePane("thread")}
        >
          Thread
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === "sidebar"}
          className={mobilePane === "sidebar" ? "active" : ""}
          onClick={() => setMobilePane("sidebar")}
        >
          Chats
        </button>
      </div>
      <div className={`chat-layout chat-layout--${mobilePane}`}>
        <ChatSidebar
          caseId={caseId}
          conversations={visibleConversations}
          activeId={chat.activeId}
          search={chat.search}
          scope={chat.scope}
          loaded={visibleLoaded}
          mineCount={chat.conversationsByScope.mine.length}
          sharedCount={chat.conversationsByScope.shared.length}
          disableNew={readyCount === 0}
          canExport={activeHydrated && activeTurns.length > 0}
          onLogTime={canLogTime ? () => setTimeEntryOpen(true) : undefined}
          onExport={exportActiveChatPdf}
          onScope={(s) => setScope(caseId, s)}
          onSearch={(v) => setSearch(caseId, v)}
          onNew={() => {
            newChat(caseId);
            setMobilePane("thread");
          }}
          onSelect={(id) => {
            void selectConversation(caseId, id);
            setMobilePane("thread");
          }}
          onRename={(id, title) => void renameConversation(caseId, id, title)}
          onDelete={(id) => void deleteConversation(caseId, id)}
          onShare={(id, shared) => void setShared(caseId, id, shared)}
        />
        <ChatThreadView
          caseId={caseId}
          readyCount={readyCount}
          active={active}
          // composer state — falls through to the "new chat" placeholder when
          // nothing is selected.
          draft={activeThread?.draft ?? chat.newDraft}
          mode={(activeThread?.mode as ChatMode) ?? chat.newMode}
          turns={activeTurns}
          hydrated={activeHydrated}
          isNewChat={chat.activeId == null}
          activeConv={activeConv}
          activeConversationId={chat.activeId}
          readOnly={isReadOnly}
          onDraftChange={(v) => setDraft(caseId, v)}
          onModeChange={(m) => setMode(caseId, m)}
          onAsk={(q, m) => ask(caseId, q, m)}
          onTimelineSaved={onTimelineSaved}
          savedNotesIndex={savedNotesIndex}
          onSaveNote={onSaveNote}
          onOpenNote={onOpenNote}
          onCaptureSelection={onCaptureSelection}
          onExportPdf={exportActiveChatPdf}
        />
      </div>
      {timeEntryOpen && (
        <TimeEntryModal
          defaultCaseId={caseId}
          onClose={() => setTimeEntryOpen(false)}
        />
      )}
    </div>
  );
}

function findConversation(
  byScope: Record<ChatScope, Conversation[]>,
  id: number,
): Conversation | null {
  for (const scope of ["mine", "shared", "all"] as ChatScope[]) {
    const found = byScope[scope].find((c) => c.id === id);
    if (found) return found;
  }
  return null;
}

// Pre-canned starter questions for an empty chat. The `label` is the chip
// the user clicks; the `question` is what's actually sent to the model;
// the `hint` is a one-line explanation rendered under the label so the
// user knows what they're going to get. `mode` overrides the composer's
// current mode for this single click — timeline questions go through
// timeline mode, exploratory strategy questions through insights, etc.
interface SuggestedQuestion {
  label: string;
  hint: string;
  question: string;
  mode: ChatMode;
}

const SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  {
    label: "Summarize this case",
    hint: "Parties, claims, key dates, and current posture.",
    question:
      "Provide a concise summary of this case grounded in the documents — who the parties are, the central claims or allegations, the most important dates, and the current procedural posture. Cite the documents you rely on.",
    mode: "both",
  },
  {
    label: "Who are the parties?",
    hint: "Roles, relationships, and counsel of record.",
    question:
      "List every party named in the documents and describe each one's role in this matter. Include counsel of record if it appears in the documents.",
    mode: "qa",
  },
  {
    label: "Build a timeline",
    hint: "Chronological events extracted from the documents.",
    question:
      "Build a chronological timeline of the most important events in this case based strictly on the documents. Include the date, what happened, and the source for each event.",
    mode: "timeline",
  },
  {
    label: "Main claims & defenses",
    hint: "Theories of liability and any defenses raised.",
    question:
      "What are the central legal claims being asserted in this case, and what defenses (if any) appear in the documents? Cite the documents that establish each claim or defense.",
    mode: "qa",
  },
  {
    label: "Strongest evidence",
    hint: "Where each side is best supported by the record.",
    question:
      "Looking across all of the documents, what evidence most strongly supports each side's position? Identify the top supporting items for each side and explain why they matter.",
    mode: "insights",
  },
  {
    label: "Gaps & weaknesses",
    hint: "Inconsistencies and missing evidence to chase down.",
    question:
      "Where are the gaps, inconsistencies, or weak points in the evidence so far? Flag anything that would benefit from additional documents, depositions, or follow-up.",
    mode: "insights",
  },
];

// ---------- shared icon ------------------------------------------------------

function ClockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SharedIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="4" cy="8" r="2" />
      <circle cx="12" cy="3.5" r="2" />
      <circle cx="12" cy="12.5" r="2" />
      <path d="M5.7 7l4.6-2.6M5.7 9l4.6 2.6" />
    </svg>
  );
}

// ---------- sidebar ----------------------------------------------------------

function ChatSidebar({
  caseId,
  conversations,
  activeId,
  search,
  scope,
  loaded,
  mineCount,
  sharedCount,
  disableNew = false,
  canExport = false,
  onLogTime,
  onExport,
  onScope,
  onSearch,
  onNew,
  onSelect,
  onRename,
  onDelete,
  onShare,
}: {
  caseId: number;
  conversations: Conversation[];
  activeId: number | null;
  search: string;
  scope: ChatScope;
  loaded: boolean;
  mineCount: number;
  sharedCount: number;
  // Disabled when the case has zero ready documents — a chat without
  // ingested evidence can't be grounded, so we steer the user to upload
  // first instead of letting them open an empty thread.
  disableNew?: boolean;
  canExport?: boolean;
  // Optional "quick log time" handler — when set, a small clock icon is
  // shown next to the "Case chats" eyebrow so users who routinely log
  // billable time can open the modal without going through the profile
  // menu. Left undefined for roles (owner/admin) that can't log time.
  onLogTime?: () => void;
  onExport: () => void;
  onScope: (s: ChatScope) => void;
  onSearch: (v: string) => void;
  onNew: () => void;
  onSelect: (id: number) => void;
  onRename: (id: number, title: string) => void;
  onDelete: (id: number) => void;
  onShare: (id: number, shared: boolean) => void;
}) {
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");

  function commitRename(id: number) {
    const v = renameValue.trim();
    if (v) onRename(id, v);
    setRenamingId(null);
    setRenameValue("");
  }

  return (
    <aside className="chat-sidebar">
      <div className="chat-sidebar__top">
        <div className="chat-sidebar__eyebrow-row">
          <div className="chat-sidebar__eyebrow">Case chats</div>
          {onLogTime && (
            <button
              type="button"
              className="chat-sidebar__time-btn"
              onClick={onLogTime}
              aria-label="Log time & mileage for this case"
              title="Log time & mileage for this case"
            >
              <ClockIcon />
            </button>
          )}
        </div>
        <div className="chat-sidebar__title-row">
          <div>
            <div className="chat-sidebar__heading">Conversations</div>
            <div className="chat-sidebar__subhead">Private to you unless shared with the case.</div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn--primary chat-sidebar__new"
          onClick={onNew}
          disabled={disableNew}
          title={
            disableNew
              ? "Upload and process documents before starting a new chat."
              : "Start a new chat in this case"
          }
        >
          + New chat
        </button>
        <button
          type="button"
          className="btn btn--ghost chat-sidebar__export"
          onClick={onExport}
          disabled={!canExport}
          title={
            canExport
              ? "Export the active chat transcript as a PDF"
              : "Select a chat with messages before exporting"
          }
        >
          Export chat PDF
        </button>
      </div>

      <div className="chat-sidebar__tabs" role="tablist" aria-label="Chat scope">
        <button
          type="button"
          role="tab"
          aria-selected={scope === "mine"}
          className={`chat-sidebar__tab${scope === "mine" ? " chat-sidebar__tab--active" : ""}`}
          onClick={() => onScope("mine")}
        >
          Mine
          <span className="chat-sidebar__tab-count">{mineCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scope === "shared"}
          className={`chat-sidebar__tab${scope === "shared" ? " chat-sidebar__tab--active" : ""}`}
          onClick={() => onScope("shared")}
          title="Chats other case members have shared with the team"
        >
          <SharedIcon />
          Shared
          <span className="chat-sidebar__tab-count">{sharedCount}</span>
        </button>
      </div>

      <div className="chat-sidebar__search">
        <input
          type="text"
          placeholder={scope === "shared" ? "Search shared chats…" : "Search this case's chats…"}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        <Link
          to={`/cases/${caseId}/chats`}
          className="chat-sidebar__advanced"
          title="Open the Chat Search page for richer filters"
        >
          Advanced search →
        </Link>
      </div>

      <div className="chat-sidebar__list" role="list">
        {!loaded && <div className="muted chat-sidebar__hint">Loading chats…</div>}
        {loaded && conversations.length === 0 && (
          <div className="muted chat-sidebar__hint">
            {search.trim()
              ? "No chats match that search."
              : scope === "shared"
                ? "No shared chats yet. When a teammate shares one of theirs, it'll show up here."
                : "No chats yet. Ask a question to start one."}
          </div>
        )}
        {conversations.map((c) => {
          const isActive = c.id === activeId;
          const title = (c.title?.trim() || c.last_question?.trim() || "Untitled chat");
          return (
            <div
              key={c.id}
              role="listitem"
              className={`chat-sidebar__item${isActive ? " chat-sidebar__item--active" : ""}`}
              onClick={() => onSelect(c.id)}
            >
              {renamingId === c.id ? (
                <input
                  className="chat-sidebar__rename"
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(c.id);
                    if (e.key === "Escape") {
                      setRenamingId(null);
                      setRenameValue("");
                    }
                  }}
                  onBlur={() => commitRename(c.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="chat-sidebar__title-line">
                  <div className="chat-sidebar__title" title={title}>{title}</div>
                  {c.is_shared && (
                    <span
                      className="chat-sidebar__shared-badge"
                      title={c.is_owner ? "Shared with the case" : `Shared by ${c.owner_display_name ?? "another user"}`}
                    >
                      <SharedIcon />
                    </span>
                  )}
                </div>
              )}
              <div className="chat-sidebar__meta">
                <span>{c.message_count} msg{c.message_count === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>{relativeTime(c.last_message_at ?? c.updated_at)}</span>
                {!c.is_owner && c.owner_display_name && (
                  <>
                    <span>·</span>
                    <span title={c.owner_email ?? undefined}>by {c.owner_display_name}</span>
                  </>
                )}
              </div>
              <div className="chat-sidebar__actions" onClick={(e) => e.stopPropagation()}>
                {c.is_owner ? (
                  <>
                    <button
                      type="button"
                      className="chat-sidebar__icon"
                      title="Rename"
                      onClick={() => {
                        setRenamingId(c.id);
                        setRenameValue(c.title ?? "");
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className={`chat-sidebar__icon${c.is_shared ? " chat-sidebar__icon--on" : ""}`}
                      title={c.is_shared
                        ? "This chat is shared with case members. Click to make it private."
                        : "Share this chat with everyone on the case (read-only for them)."}
                      onClick={() => onShare(c.id, !c.is_shared)}
                    >
                      {c.is_shared ? "Unshare" : "Share"}
                    </button>
                    <button
                      type="button"
                      className="chat-sidebar__icon chat-sidebar__icon--danger"
                      title="Delete chat"
                      onClick={() => {
                        if (confirm(`Delete chat "${title}"? This cannot be undone.`)) {
                          onDelete(c.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <span className="chat-sidebar__readonly-tag">Read-only</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function relativeTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (!Number.isFinite(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

// ---------- thread view (composer + transcript) ------------------------------

function ChatThreadView({
  caseId,
  readyCount,
  active,
  draft,
  mode,
  turns,
  hydrated,
  isNewChat,
  activeConv,
  activeConversationId,
  readOnly,
  onDraftChange,
  onModeChange,
  onAsk,
  onTimelineSaved,
  savedNotesIndex,
  onSaveNote,
  onOpenNote,
  onCaptureSelection,
  onExportPdf,
}: {
  caseId: number;
  readyCount: number;
  active: boolean;
  draft: string;
  mode: ChatMode;
  turns: ChatTurn[];
  hydrated: boolean;
  isNewChat: boolean;
  activeConv: Conversation | null;
  activeConversationId: number | null;
  readOnly: boolean;
  onDraftChange: (v: string) => void;
  onModeChange: (m: ChatMode) => void;
  onAsk: (question: string, mode: ChatMode) => Promise<void>;
  onTimelineSaved?: () => void;
  savedNotesIndex?: Map<string, number>;
  onSaveNote?: (draft: NoteDraft) => Promise<number>;
  onOpenNote?: (noteId: number) => void;
  onCaptureSelection?: (draft: NoteDraft) => void;
  onExportPdf: () => void;
}) {
  const [atBottom, setAtBottom] = useState(true);
  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // ----- Highlight-to-capture: watch for text selections inside the chat
  // log and surface a floating "Save selection to note" pill anchored to
  // the bottom of the selection rect. The pill captures source metadata
  // (query_id, conversation_id, parent_question) from the closest enclosing
  // turn via data-* attributes so the caller can build a back-link.
  const [selectionPopover, setSelectionPopover] =
    useState<SelectionPopoverState | null>(null);
  // The "expanded" mode renders an inline mini-form at the same anchor so
  // the user can set title/category/tags and save WITHOUT leaving the
  // chat. Decoupled from `selectionPopover` so dismissing the live
  // selection (clicking outside) doesn't kill an in-progress capture.
  const [expandedCapture, setExpandedCapture] =
    useState<SelectionPopoverState | null>(null);
  // Cached per-case category / tag suggestions, used to autocomplete the
  // popover form. Best-effort — empty arrays just means no suggestions.
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  // Suppress reopening while we're handing off to the editor (otherwise
  // clearing the selection re-fires selectionchange and flickers the pill).
  const handingOffRef = useRef(false);

  useEffect(() => {
    if (!onCaptureSelection) return;
    let cancel = false;
    (async () => {
      try {
        const [cats, tags] = await Promise.all([
          api.get<string[]>(`/cases/${caseId}/notes/categories`),
          api.get<string[]>(`/cases/${caseId}/notes/tags`),
        ]);
        if (cancel) return;
        setAllCategories(cats);
        setAllTags(tags);
      } catch {
        /* metadata is best-effort; the form still works without it. */
      }
    })();
    return () => { cancel = true; };
  }, [caseId, onCaptureSelection]);

  useEffect(() => {
    if (!onCaptureSelection) return;
    const log = logRef.current;
    if (!log) return;

    function update() {
      if (handingOffRef.current) return;
      // While the inline capture form is open the user is interacting
      // with form controls — text selections inside the form (or even
      // re-selections in the chat) shouldn't replace the pill or steal
      // focus from the popover.
      if (expandedCapture) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelectionPopover(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const anchor = range.commonAncestorContainer as Node;
      if (!log!.contains(anchor)) {
        // Selection started/ended outside the chat log (sidebar, composer,
        // etc.) — leave it alone.
        setSelectionPopover(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 4) {
        // Tiny selections are almost always accidental clicks/double-clicks.
        setSelectionPopover(null);
        return;
      }
      // Walk up to find the enclosing turn so we can attribute the snippet.
      const startEl =
        range.startContainer.nodeType === 1
          ? (range.startContainer as Element)
          : (range.startContainer.parentElement as Element | null);
      const turnEl = startEl?.closest("[data-query-id]") as HTMLElement | null;
      if (!turnEl) {
        setSelectionPopover(null);
        return;
      }
      const queryId = Number(turnEl.dataset.queryId);
      const question = turnEl.dataset.question ?? "";
      const turnMode = (turnEl.dataset.mode ?? "qa") as ChatMode;

      // Capture the selection's VIEWPORT rect; both pill and popover are
      // `position: fixed`, so they're never clipped by .chat__log's
      // overflow box (which was the bug where the Save button at the
      // bottom of the popover sat outside the visible chat area).
      const rect = range.getBoundingClientRect();
      setSelectionPopover({
        text,
        queryId: Number.isFinite(queryId) ? queryId : null,
        question,
        turnMode,
        selRect: {
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
        },
      });
    }

    document.addEventListener("selectionchange", update);
    // Also catch the case where the user finishes a drag-select with a
    // mouseup outside the log (Chrome doesn't always fire selectionchange).
    log.addEventListener("mouseup", update);
    return () => {
      document.removeEventListener("selectionchange", update);
      log.removeEventListener("mouseup", update);
    };
  }, [onCaptureSelection, expandedCapture]);

  // When the user switches conversations or scrolls, dismiss any stale
  // pill / form. We close the form too because its anchor (and the chat
  // turn it was capturing from) may no longer be visible.
  useEffect(() => {
    setSelectionPopover(null);
    setExpandedCapture(null);
  }, [activeConversationId]);

  // Click on the small "+ Save selection to note" pill: expand into an
  // inline mini-form at the same anchor so the user can set title /
  // category / tags and save in place. We collapse the live selection
  // here so the form fields can take focus without the browser's
  // selection visually fighting the popover.
  function handleExpandCapture() {
    if (!selectionPopover) return;
    handingOffRef.current = true;
    setExpandedCapture(selectionPopover);
    setSelectionPopover(null);
    requestAnimationFrame(() => {
      window.getSelection()?.removeAllRanges();
      handingOffRef.current = false;
    });
  }

  function dismissExpandedCapture() {
    setExpandedCapture(null);
  }

  // "Open in editor" escape hatch from inside the popover — hands the
  // current draft off to the Notes tab for the full editor (sharing,
  // mentions, body editing, etc.). Same channel as before.
  function handoffToNotesEditor(draft: NoteDraft) {
    setExpandedCapture(null);
    onCaptureSelection?.(draft);
  }

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  function scrollToBottom(behavior: ScrollBehavior = "smooth") {
    const el = logRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }

  const turnsLen = turns.length;
  const lastProgress = turns[turnsLen - 1]?.progress_pct ?? 0;

  useLayoutEffect(() => {
    if (atBottom) scrollToBottom("smooth");
  }, [turnsLen, lastProgress, atBottom]);

  useEffect(() => {
    if (active) {
      scrollToBottom("auto");
      if (!readOnly) inputRef.current?.focus();
    }
  }, [active, isNewChat, readOnly]);

  function onLogScroll() {
    const el = logRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    setAtBottom(distance < 24);
    // The popover is anchored to a viewport rect that becomes stale once
    // the log scrolls, so dismiss it rather than render in the wrong spot.
    if (selectionPopover) setSelectionPopover(null);
  }

  async function submitDraft() {
    const q = draft.trim();
    if (!q || readyCount === 0 || readOnly) return;
    onDraftChange("");
    setAtBottom(true);
    await onAsk(q, mode);
  }

  // One-click "starter" questions shown in the empty-chat state. Each
  // suggestion bypasses the textarea entirely, switches the chat mode to
  // whichever fits the question best (timeline questions go to "timeline",
  // strategy/exploratory questions to "insights", etc.), and fires the
  // same `onAsk` path as a typed question.
  async function submitSuggestion(question: string, suggestionMode: ChatMode) {
    if (readyCount === 0 || readOnly) return;
    onDraftChange("");
    onModeChange(suggestionMode);
    setAtBottom(true);
    await onAsk(question, suggestionMode);
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    await submitDraft();
  }

  const activeTurns = useMemo(() => turns.filter((t) => isActiveStatus(t.status)), [turns]);
  const queueSummary = summarizeQueue(activeTurns);
  const submitHint = readOnly
    ? `You're viewing a shared chat${activeConv?.owner_display_name ? ` from ${activeConv.owner_display_name}` : ""}. Only the owner can post replies.`
    : readyCount === 0
      ? "Upload and process documents to enable chat."
      : activeTurns.length > 0
        ? `${activeTurns.length} question(s) are running. New prompts will queue automatically.`
        : mode === "insights"
          ? "Exploratory mode — pulls a wider net of evidence and lets the model draw connections. Treat results as a brainstorm, not as verified facts."
          : "Press Enter to send, Shift+Enter for a new line.";
  const conversationTitle = activeConv?.title?.trim()
    || activeConv?.last_question?.trim()
    || (isNewChat ? "New chat" : "Untitled chat");
  const exportedAt = formatExportedAt(new Date());

  return (
    <div className="chat">
      {readOnly && activeConv && (
        <div className="chat__readonly-banner" role="note">
          <SharedIcon />
          <span>
            Read-only: shared by{" "}
            <strong>{activeConv.owner_display_name ?? "another user"}</strong>
          </span>
        </div>
      )}
      <div className="chat__toolbar no-print">
        <div className="chat__toolbar-copy">
          <span className="chat__toolbar-title">{conversationTitle}</span>
          <span className="muted">
            {hydrated ? `${turns.length} turn${turns.length === 1 ? "" : "s"}` : "Loading transcript"}
          </span>
        </div>
        <button
          type="button"
          className="btn btn--small"
          onClick={onExportPdf}
          disabled={!hydrated || turns.length === 0}
          title="Open print dialog - choose Save as PDF"
        >
          Export PDF
        </button>
      </div>
      <div className="chat__log" ref={logRef} onScroll={onLogScroll}>
        <div className="chat-print-header print-only">
          <h2>{conversationTitle}</h2>
          <div className="muted">
            Case #{caseId} chat transcript · {turns.length} turn{turns.length === 1 ? "" : "s"} · exported {exportedAt}
            {activeConv?.owner_display_name && <> · owner: {activeConv.owner_display_name}</>}
          </div>
        </div>
        {!hydrated && (
          <div className="empty">Loading conversation…</div>
        )}
        {hydrated && turns.length === 0 && (
          <div className="empty chat__empty">
            {readyCount === 0 ? (
              <div className="chat__empty-lead">
                <Link to={`/cases/${caseId}?tab=docs`}>Click here</Link> to upload a document before you start chatting
              </div>
            ) : isNewChat ? (
              <>
                <div className="chat__empty-lead">
                  New chat. Pick a starting point below — or type your own question. Your first question names the thread.
                </div>
                <div className="chat__suggestions" role="list">
                  {SUGGESTED_QUESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      role="listitem"
                      className="chat__suggestion"
                      disabled={readOnly}
                      onClick={() => void submitSuggestion(s.question, s.mode)}
                      title={s.question}
                    >
                      <span className="chat__suggestion-label">{s.label}</span>
                      <span className="chat__suggestion-hint">{s.hint}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : readOnly ? (
              <div className="chat__empty-lead">This shared chat doesn't have any messages yet.</div>
            ) : (
              <div className="chat__empty-lead">
                Ask about parties, dates, obligations, timelines — answers are grounded to source spans.
              </div>
            )}
          </div>
        )}
        {turns.map((t) => (
          <TurnView
            key={t.query_id}
            turn={t}
            caseId={caseId}
            conversationId={activeConversationId}
            onTimelineSaved={onTimelineSaved}
            savedNoteId={savedNotesIndex?.get(`chat:${t.query_id}`) ?? null}
            onSaveNote={onSaveNote}
            onOpenNote={onOpenNote}
          />
        ))}
        {selectionPopover && onCaptureSelection && !expandedCapture && (
          <button
            type="button"
            className="chat__selection-pill"
            style={{
              // Anchor the pill to the bottom-centre of the selection in
              // VIEWPORT coordinates. `position: fixed` keeps it visible
              // even when the selection is near the bottom of the chat
              // log (where absolute-in-overflow would clip it).
              top: selectionPopover.selRect.bottom + 8,
              left:
                (selectionPopover.selRect.left + selectionPopover.selRect.right) / 2,
            }}
            // Use mouseDown (not click) and preventDefault so the textarea/
            // selection isn't collapsed out from under us before our handler
            // runs — we want the captured text intact.
            onMouseDown={(ev) => {
              ev.preventDefault();
              handleExpandCapture();
            }}
            title="Save this selection as a new note (you can set tags / category)"
          >
            + Save selection to note
          </button>
        )}
        {expandedCapture && (
          <SelectionCapturePopover
            state={expandedCapture}
            conversationId={activeConversationId}
            allCategories={allCategories}
            allTags={allTags}
            onSave={onSaveNote}
            onOpenNote={onOpenNote}
            onOpenInEditor={onCaptureSelection ? handoffToNotesEditor : undefined}
            onClose={dismissExpandedCapture}
          />
        )}
      </div>

      {!atBottom && turns.length > 0 && (
        <button
          type="button"
          className="chat__jump"
          onClick={() => { setAtBottom(true); scrollToBottom("smooth"); }}
        >
          Jump to latest ↓
        </button>
      )}

      {queueSummary && (
        <div className="chat__queue">
          <span className="chat__queue-dot" />
          {queueSummary}
        </div>
      )}

      <form className="chat__bar" onSubmit={onSubmit}>
        <div className="chat__composer">
          <div className="chat__input-wrap">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim() && readyCount > 0 && !readOnly) {
                    void submitDraft();
                  }
                }
              }}
              disabled={readOnly || readyCount === 0}
              placeholder={
                readOnly
                  ? "This is a read-only shared chat."
                  : readyCount === 0
                    ? "Upload a document to enable chat."
                    : activeTurns.length > 0
                      ? "Type another question - it will queue up"
                      : isNewChat
                        ? "Start a new chat - what would you like to know?"
                        : "e.g. What did the defendant know about the inspection report?"
              }
            />
            <select
              className="chat__mode"
              value={mode}
              onChange={(e) => onModeChange(e.target.value as ChatMode)}
              disabled={readOnly || readyCount === 0}
              title={
                mode === "insights"
                  ? "Exploratory mode — encourages cross-document inferences. May speculate beyond the literal evidence."
                  : "Choose how this question should be answered."
              }
            >
              <option value="both">Answer + timeline</option>
              <option value="qa">Just answer</option>
              <option value="timeline">Just timeline</option>
              <option value="insights">Insights (exploratory)</option>
            </select>
            <button
              type="submit"
              className="btn btn--primary chat__submit"
              disabled={!draft.trim() || readyCount === 0 || readOnly}
              title={
                readOnly
                  ? "Read-only — only the chat's owner can post."
                  : readyCount === 0
                    ? "Upload and process documents to enable chat."
                    : activeTurns.length > 0
                      ? `${activeTurns.length} question(s) still running - yours will queue.`
                      : undefined
              }
            >
              {activeTurns.length > 0 ? "Queue" : "Ask"}
            </button>
          </div>
          <div className="chat__composer-hint muted">{submitHint}</div>
        </div>
      </form>
    </div>
  );
}

interface SelectionPopoverState {
  text: string;
  queryId: number | null;
  question: string;
  turnMode: ChatMode;
  /** Selection rect in VIEWPORT coordinates (i.e. matches `position: fixed`).
   *  Stored as the rect itself so the popover can position above OR below
   *  the highlight depending on which side has room. */
  selRect: { top: number; bottom: number; left: number; right: number };
}

function buildSelectionNoteDraft(opts: {
  selection: string;
  queryId: number | null;
  conversationId: number | null;
  parentQuestion: string;
  turnMode: ChatMode;
  /** Overrides set in the inline capture form. Falsy values fall back to
   *  the same auto-generated defaults the original draft used. */
  title?: string;
  category?: string;
  tags?: string[];
}): NoteDraft {
  const { selection, queryId, conversationId, parentQuestion, turnMode } = opts;
  const isInsights = turnMode === "insights";
  const lines: string[] = [];
  if (isInsights) {
    lines.push("> ⚠ Captured from **Insights (exploratory)** chat mode — may include inferences beyond the literal evidence. Verify before relying on specific claims.");
    lines.push("");
  }
  // Render the highlighted text as a blockquote so it's visually distinct
  // from anything the user adds afterwards. Multi-line selections each
  // become their own quoted line.
  for (const line of selection.split(/\r?\n/)) {
    lines.push(`> ${line}`);
  }
  if (parentQuestion) {
    lines.push("");
    lines.push(`*From chat — Q:* "${truncatePlain(parentQuestion, 240)}"`);
  }

  const ref: Record<string, unknown> = {
    kind: "selection",
    selection_text: selection,
  };
  if (queryId != null) ref.query_id = queryId;
  if (conversationId != null) ref.conversation_id = conversationId;
  if (parentQuestion) ref.parent_question = parentQuestion;

  const tagsBase = isInsights ? ["chat", "insights"] : ["chat"];
  return {
    title: (opts.title ?? "").trim() || `Excerpt: ${truncatePlain(selection, 60)}`,
    body: lines.join("\n"),
    category: opts.category?.trim() || undefined,
    tags: opts.tags ?? tagsBase,
    source_type: "chat",
    source_ref: ref,
  };
}

/** Inline capture popover anchored at the same spot as the selection pill.
 *  Lets the user fill in title / category / tags and save in place — the
 *  whole interaction stays inside the chat workspace. The "Open in editor"
 *  escape hatch hands the same draft to the Notes tab for advanced edits
 *  (sharing, mentions, free-form body, etc.). */
function SelectionCapturePopover({
  state,
  conversationId,
  allCategories,
  allTags,
  onSave,
  onOpenNote,
  onOpenInEditor,
  onClose,
}: {
  state: SelectionPopoverState;
  conversationId: number | null;
  allCategories: string[];
  allTags: string[];
  onSave?: (draft: NoteDraft) => Promise<number>;
  /** Jump to the Notes tab and open the note we just created. */
  onOpenNote?: (noteId: number) => void;
  onOpenInEditor?: (draft: NoteDraft) => void;
  onClose: () => void;
}) {
  const isInsights = state.turnMode === "insights";
  const [title, setTitle] = useState<string>(
    `Excerpt: ${truncatePlain(state.text, 60)}`,
  );
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState<string[]>(
    isInsights ? ["chat", "insights"] : ["chat"],
  );
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [popHeight, setPopHeight] = useState<number>(360);

  // Esc dismisses. We deliberately do NOT close on outside-click here:
  // (a) racing with the Save button's mousedown was hiding the click,
  // (b) the user explicitly opted in to this dialog and a misclick away
  //     shouldn't lose their typed title/tags. Close button + Esc only.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Measure the rendered popover so the above/below flip math has an
  // accurate height to work with on the *current* form state (the saved
  // confirmation view is shorter than the form view, etc.).
  useEffect(() => {
    if (popRef.current) {
      setPopHeight(popRef.current.offsetHeight);
    }
  }, [savedId, err, tags.length]);

  // ----- positioning ------------------------------------------------------
  // Both axes are in viewport coords (we render `position: fixed`). We
  // prefer placing the popover BELOW the selection, but flip ABOVE if
  // there isn't enough room — and clamp horizontally to the viewport.
  const POP_WIDTH = Math.min(360, window.innerWidth - 24);
  const MARGIN = 12;
  const spaceBelow = window.innerHeight - state.selRect.bottom;
  const placeAbove = spaceBelow < popHeight + MARGIN
    && state.selRect.top > spaceBelow;
  const top = placeAbove
    ? Math.max(MARGIN, state.selRect.top - popHeight - 8)
    : Math.min(window.innerHeight - popHeight - MARGIN, state.selRect.bottom + 8);
  const centerX = (state.selRect.left + state.selRect.right) / 2;
  const left = Math.max(
    MARGIN + POP_WIDTH / 2,
    Math.min(window.innerWidth - MARGIN - POP_WIDTH / 2, centerX),
  );

  function commitTag() {
    const v = tagInput.trim().replace(/^#/, "");
    if (!v) return;
    if (!tags.some((t) => t.toLowerCase() === v.toLowerCase())) {
      setTags([...tags, v.slice(0, 64)]);
    }
    setTagInput("");
  }

  function buildDraft(): NoteDraft {
    return buildSelectionNoteDraft({
      selection: state.text,
      queryId: state.queryId,
      conversationId,
      parentQuestion: state.question,
      turnMode: state.turnMode,
      title,
      category,
      tags,
    });
  }

  async function handleSave() {
    if (!onSave || saving || savedId != null) return;
    setSaving(true);
    setErr(null);
    try {
      // Make sure any tag the user typed but didn't press Enter on still
      // gets included — this is the most common "I clicked Save and lost
      // my tag" papercut on chip inputs.
      const pendingTag = tagInput.trim().replace(/^#/, "");
      const finalTags = pendingTag &&
        !tags.some((t) => t.toLowerCase() === pendingTag.toLowerCase())
          ? [...tags, pendingTag.slice(0, 64)]
          : tags;
      if (pendingTag) {
        setTags(finalTags);
        setTagInput("");
      }
      const id = await onSave(
        buildSelectionNoteDraft({
          selection: state.text,
          queryId: state.queryId,
          conversationId,
          parentQuestion: state.question,
          turnMode: state.turnMode,
          title,
          category,
          tags: finalTags,
        }),
      );
      setSavedId(id);
      // Stay open with a "Saved to notes" confirmation so the user has
      // unambiguous feedback and a chance to jump to the new note.
    } catch (e: any) {
      setErr(e?.message ?? "Failed to save note");
    } finally {
      setSaving(false);
    }
  }

  function handleOpenInEditor() {
    if (!onOpenInEditor) return;
    onOpenInEditor(buildDraft());
  }

  // ----- Saved confirmation view -----------------------------------------
  if (savedId != null) {
    return (
      <div
        ref={popRef}
        className="chat__capture-pop chat__capture-pop--saved"
        style={{ top, left, width: POP_WIDTH }}
        role="dialog"
        aria-label="Saved to notes"
      >
        <header className="chat__capture-pop__head chat__capture-pop__head--ok">
          <span className="chat__capture-pop__title">✓ Saved to notes</span>
          <button
            type="button"
            className="chat__capture-pop__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >×</button>
        </header>
        <div className="chat__capture-pop__body">
          <div className="chat__capture-pop__snippet" title={state.text}>
            "{state.text}"
          </div>
          <p className="chat__capture-pop__saved-msg muted">
            Captured as a new note in <strong>Notes</strong>
            {category && <> · category <strong>{category}</strong></>}
            . You can keep working in the chat.
          </p>
          {tags.length > 0 && (
            <div className="chat__capture-pop__tags chat__capture-pop__tags--readonly">
              {tags.map((t) => (
                <span key={t} className="chip chip--on">#{t}</span>
              ))}
            </div>
          )}
        </div>
        <footer className="chat__capture-pop__foot">
          <span className="chat__capture-pop__status chat__capture-pop__status--ok">
            Saved ✓
          </span>
          <div className="chat__capture-pop__actions">
            {onOpenNote && (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => { onOpenNote(savedId); onClose(); }}
                title="Open the saved note in the Notes tab"
              >
                Open note
              </button>
            )}
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={onClose}
              autoFocus
            >
              Done
            </button>
          </div>
        </footer>
      </div>
    );
  }

  // ----- Form view --------------------------------------------------------
  return (
    <div
      ref={popRef}
      className="chat__capture-pop"
      style={{ top, left, width: POP_WIDTH }}
      role="dialog"
      aria-label="Save selection to note"
    >
      <header className="chat__capture-pop__head">
        <span className="chat__capture-pop__title">Save selection to note</span>
        <button
          type="button"
          className="chat__capture-pop__close"
          onClick={onClose}
          aria-label="Close"
          title="Close (Esc)"
        >×</button>
      </header>

      <div className="chat__capture-pop__body">
        <div className="chat__capture-pop__snippet" title={state.text}>
          "{state.text}"
        </div>

        <label className="chat__capture-pop__field">
          <span>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Optional title"
            autoFocus
          />
        </label>

        <label className="chat__capture-pop__field">
          <span>Category</span>
          <input
            type="text"
            list="chat-capture-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Strategy, Research, Witness…"
          />
          <datalist id="chat-capture-categories">
            {allCategories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>

        <div className="chat__capture-pop__field">
          <span>Tags</span>
          <div className="chat__capture-pop__tags">
            {tags.map((t) => (
              <span key={t} className="chip chip--on">
                #{t}
                <button
                  type="button"
                  className="chip__x"
                  aria-label={`Remove ${t}`}
                  onClick={() => setTags(tags.filter((x) => x !== t))}
                >×</button>
              </span>
            ))}
            <input
              className="chat__capture-pop__tag-input"
              type="text"
              list="chat-capture-tags"
              placeholder={tags.length === 0 ? "Add tags (Enter)" : "Add"}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  commitTag();
                } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
                  setTags(tags.slice(0, -1));
                }
              }}
              onBlur={commitTag}
            />
            <datalist id="chat-capture-tags">
              {allTags.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
        </div>
      </div>

      <footer className="chat__capture-pop__foot">
        <span
          className={
            "chat__capture-pop__status" +
            (err ? " chat__capture-pop__status--err" : "")
          }
        >
          {err
            ? err
            : saving
              ? "Saving…"
              : `${state.text.length} chars`}
        </span>
        <div className="chat__capture-pop__actions">
          {onOpenInEditor && (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={handleOpenInEditor}
              disabled={saving}
              title="Open in the full Notes editor (sharing, mentions, body editing)"
            >
              Open in editor
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={handleSave}
            disabled={!onSave || saving}
            title="Save this snippet as a new note and stay in the chat"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </footer>
    </div>
  );
}

function truncatePlain(s: string, n: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

function isActiveStatus(s: ChatTurn["status"]): boolean {
  return s === "queued" || s === "running" || s === "pending";
}

function summarizeQueue(active: ChatTurn[]): string | null {
  if (active.length === 0) return null;
  const running = active.find((t) => t.status === "running");
  const queued = active.filter((t) => t.status !== "running");
  const parts: string[] = [];
  if (running) {
    parts.push(`Running: ${truncate(running.question, 60)}`);
  }
  if (queued.length === 1) {
    parts.push(`1 queued behind it`);
  } else if (queued.length > 1) {
    parts.push(`${queued.length} queued behind it`);
  }
  return parts.join(" · ");
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function formatExportedAt(d: Date): string {
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function buildChatExportHtml({
  caseId,
  title,
  ownerName,
  turns,
  exportedAt,
}: {
  caseId: number;
  title: string;
  ownerName: string | null;
  turns: ChatTurn[];
  exportedAt: Date;
}): string {
  const completedTurns = turns.filter((t) => t.status === "complete" || t.answer || t.timeline.length > 0);
  const body = completedTurns.map((turn, idx) => renderExportTurn(turn, idx)).join("");
  const meta = [
    `Case #${caseId}`,
    `${turns.length} turn${turns.length === 1 ? "" : "s"}`,
    `Exported ${formatExportedAt(exportedAt)}`,
    ownerName ? `Owner: ${ownerName}` : "",
  ].filter(Boolean).join(" · ");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { margin: 0.75in; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
    }
    body {
      font: 11pt/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
    }
    h1, h2, h3, p { margin-top: 0; }
    .export-header {
      margin-bottom: 22px;
      padding-bottom: 12px;
      border-bottom: 1px solid #cfcfcf;
    }
    .export-header h1 {
      margin: 0 0 6px;
      font-size: 18pt;
      line-height: 1.2;
      font-weight: 700;
    }
    .meta, .source, .sources, .timeline-meta {
      color: #555;
      font-size: 9pt;
    }
    .turn {
      margin: 0 0 24px;
      break-inside: auto;
      page-break-inside: auto;
    }
    .question {
      margin-bottom: 14px;
      padding: 9px 11px;
      border-left: 3px solid #777;
      background: #f7f7f7;
      line-height: 1.45;
      break-after: avoid;
      page-break-after: avoid;
    }
    .question-label {
      font-weight: 700;
    }
    .answer p {
      margin: 0 0 9px;
    }
    .timeline-section {
      margin-top: 18px;
      padding-top: 10px;
      border-top: 1px solid #ddd;
    }
    .timeline-section h2,
    .sources h2 {
      margin: 0 0 8px;
      color: #333;
      font-size: 10pt;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .timeline-event {
      margin: 0 0 13px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e5e5;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .timeline-date {
      margin-bottom: 3px;
      color: #555;
      font-size: 9pt;
      font-weight: 600;
    }
    .timeline-title {
      margin-bottom: 3px;
      font-weight: 700;
    }
    .timeline-desc {
      margin-bottom: 3px;
    }
    .timeline-meta {
      margin-bottom: 3px;
    }
    .source {
      margin-top: 2px;
    }
    .sources {
      margin-top: 14px;
      padding-top: 10px;
      border-top: 1px solid #ddd;
    }
    .sources ol {
      margin: 0;
      padding-left: 18px;
    }
    .sources li {
      margin-bottom: 4px;
    }
    .token {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 8pt;
      color: #666;
    }
    .status {
      color: #666;
      font-style: italic;
    }
  </style>
</head>
<body>
  <header class="export-header">
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">${escapeHtml(meta)}</div>
  </header>
  ${body || `<p class="status">No completed messages are available to export.</p>`}
</body>
</html>`;
}

function renderExportTurn(turn: ChatTurn, idx: number): string {
  const citations = uniqueCitations(turn.citations);
  return `<section class="turn">
  <div class="question"><span class="question-label">Q${idx + 1}.</span> ${escapeHtml(turn.question)}</div>
  ${turn.answer ? `<div class="answer">${renderAnswerForExport(turn.answer, turn.citations)}</div>` : ""}
  ${turn.timeline.length > 0 ? renderTimelineForExport(turn.timeline, turn.citations) : ""}
  ${citations.length > 0 ? renderSourcesForExport(citations) : ""}
</section>`;
}

function renderAnswerForExport(text: string, citations: Citation[]): string {
  const tokenRefs = citationReferenceMap(text, citations);
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n[ \t]*\n+/);
  return paragraphs
    .map((para) => `<p>${renderTextWithCitationRefs(para, tokenRefs)}</p>`)
    .join("");
}

function renderTimelineForExport(events: TimelineEvent[], citations: Citation[]): string {
  const idx = new Map(citations.map((c) => [c.provenance_token, c]));
  const items = [...events]
    .sort((a, b) => (a.event_date ?? a.event_datetime ?? "").localeCompare(b.event_date ?? b.event_datetime ?? ""))
    .map((e) => {
      const c = idx.get(e.provenance_token);
      const actors = (e.actors ?? [])
        .map((a) => `${a.name}${a.role ? `, ${a.role}` : ""}`)
        .join("; ");
      return `<article class="timeline-event">
  <div class="timeline-date">${escapeHtml(formatTimelineExportDate(e))}</div>
  <div class="timeline-title">${escapeHtml(e.headline)}</div>
  ${actors ? `<div class="timeline-meta">${escapeHtml(actors)}</div>` : ""}
  ${e.description ? `<div class="timeline-desc">${escapeHtml(e.description)}</div>` : ""}
  <div class="source">Source: ${escapeHtml(c ? sourceLabel(c) : e.provenance_token)}</div>
</article>`;
    })
    .join("");
  return `<section class="timeline-section">
  <h2>${events.length} event${events.length === 1 ? "" : "s"} from this answer</h2>
  ${items}
</section>`;
}

function renderSourcesForExport(citations: Citation[]): string {
  return `<section class="sources">
  <h2>Sources</h2>
  <ol>
    ${citations.map((c) => `<li>${escapeHtml(sourceLabel(c))}<br><span class="token">${escapeHtml(c.provenance_token)}</span></li>`).join("")}
  </ol>
</section>`;
}

function citationReferenceMap(text: string, citations: Citation[]): Map<string, number> {
  const available = new Set(citations.map((c) => c.provenance_token));
  const refs = new Map<string, number>();
  let n = 0;
  const re = /\[PT-[^\]]+\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const inner = m[0].slice(1, -1);
    for (const raw of inner.split(/\s*,\s*/)) {
      const token = raw.trim();
      if (/^PT-/.test(token) && available.has(token) && !refs.has(token)) {
        refs.set(token, ++n);
      }
    }
  }
  return refs;
}

function renderTextWithCitationRefs(text: string, refs: Map<string, number>): string {
  const parts = text.split(/(\[PT-[^\]]+\])/g);
  return parts.map((part) => {
    const m = part.match(/^\[(PT-[^\]]+)\]$/);
    if (!m) return escapeHtml(part).replace(/\n/g, "<br>");
    const labels = m[1]
      .split(/\s*,\s*/)
      .map((token) => refs.get(token.trim()))
      .filter((n): n is number => typeof n === "number");
    return labels.length ? `[${labels.join(", ")}]` : escapeHtml(part);
  }).join("");
}

function formatTimelineExportDate(e: TimelineEvent): string {
  if (e.event_datetime) return e.event_datetime.replace("T", " ");
  if (e.event_date) return e.event_date;
  return "Undated";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function TurnView({
  turn,
  caseId,
  conversationId,
  onTimelineSaved,
  savedNoteId,
  onSaveNote,
  onOpenNote,
}: {
  turn: ChatTurn;
  caseId: number;
  conversationId: number | null;
  onTimelineSaved?: () => void;
  savedNoteId: number | null;
  onSaveNote?: (draft: NoteDraft) => Promise<number>;
  onOpenNote?: (noteId: number) => void;
}) {
  const isActive = isActiveStatus(turn.status);
  const printedCitations = uniqueCitations(turn.citations);

  function buildNoteDraft(): NoteDraft {
    const lines: string[] = [];
    const isInsights = turn.mode === "insights";
    if (isInsights) {
      lines.push("> ⚠ Captured from **Insights (exploratory)** chat mode — may include inferences beyond the literal evidence. Verify before relying on specific claims.");
      lines.push("");
    }
    lines.push(`**Q.** ${turn.question}`);
    if (turn.answer) {
      lines.push("");
      lines.push(turn.answer);
    }
    if (turn.citations.length > 0) {
      lines.push("");
      lines.push("Sources:");
      for (const c of turn.citations.slice(0, 12)) {
        lines.push(`- ${c.document_name}${c.page ? ` p.${c.page}` : ""}${c.paragraph ? ` ¶${c.paragraph}` : ""} — ${c.provenance_token}`);
      }
    }
    const ref: Record<string, unknown> = {
      kind: "answer",
      query_id: turn.query_id,
      parent_question: turn.question,
    };
    if (conversationId != null) ref.conversation_id = conversationId;
    return {
      title: (isInsights ? "Insights: " : "") + turn.question.slice(0, 120),
      body: lines.join("\n"),
      tags: isInsights ? ["chat", "insights"] : ["chat"],
      source_type: "chat",
      source_ref: ref,
    };
  }

  return (
    <div
      className={`turn turn--${turn.status}${turn.mode === "insights" ? " turn--insights" : ""}`}
      data-query-id={turn.query_id}
      data-question={turn.question}
      data-mode={turn.mode}
    >
      <div className="turn__q"><strong>Q.</strong> {turn.question}</div>

      {turn.mode === "insights" && (
        <div className="turn__insights-badge" role="note" title="This response was generated in exploratory mode. The model was invited to draw connections and inferences across documents — treat it as a brainstorm, not as verified evidence.">
          <span className="turn__insights-dot" aria-hidden />
          <span>
            <strong>Insights (exploratory)</strong> — the model drew connections across {turn.citations.length || "multiple"} sources and may have inferred beyond the literal text. Verify before relying on any specific claim.
          </span>
        </div>
      )}

      {isActive && <ProgressRow turn={turn} />}

      {turn.status === "failed" && (
        <div className="alert alert--error">
          {turn.error_message ?? "Query failed"}
        </div>
      )}

      {/* Render the answer as soon as ANY text is available — including
          while the query is still streaming. The backend pushes the
          in-progress salvaged answer into queries.answer every ~400ms, the
          chat-store poller picks it up, and we re-render here. We also show
          a blinking cursor while it's still streaming so the user can see
          progress at the token level. Citation tokens won't resolve until
          the full payload lands (turn.citations is empty mid-stream), and
          AnswerText already renders unresolved [PT-...] tags as the bare
          token, which is the right behaviour. */}
      {turn.status === "running" && turn.answer && (
        <div className="turn__a turn__a--streaming">
          <AnswerText
            text={turn.answer}
            citations={turn.citations}
            caseId={caseId}
          />
          <span className="streaming-cursor" aria-hidden>▍</span>
        </div>
      )}

      {turn.status === "complete" && (
        <>
          {turn.answer && (
            <div className="turn__a">
              <AnswerText
                text={turn.answer}
                citations={turn.citations}
                caseId={caseId}
              />
            </div>
          )}
          {turn.timeline.length > 0 && (
            <MiniTimeline
              events={turn.timeline}
              citations={turn.citations}
              caseId={caseId}
              onSaved={onTimelineSaved}
            />
          )}
          {printedCitations.length > 0 && (
            <div className="turn__sources print-only">
              <div className="turn__sources-title">Sources</div>
              <ol>
                {printedCitations.map((c) => (
                  <li key={c.provenance_token}>
                    <span className="turn__source-doc">{sourceLabel(c)}</span>
                    <span className="turn__source-token">{c.provenance_token}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="turn__foot">
            <span>
              {turn.citations.length} sources
              {turn.latency_ms != null && <> · {turn.latency_ms} ms</>}
            </span>
            {onSaveNote && (
              <SaveToNotesButton
                savedNoteId={savedNoteId}
                buildDraft={buildNoteDraft}
                onSaveNote={onSaveNote}
                onOpenNote={onOpenNote}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function uniqueCitations(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of citations) {
    if (seen.has(c.provenance_token)) continue;
    seen.add(c.provenance_token);
    out.push(c);
  }
  return out;
}

function sourceLabel(c: Citation): string {
  const parts = [c.document_name];
  if (c.page != null) parts.push(`p.${c.page}`);
  if (c.paragraph != null) parts.push(`¶${c.paragraph}`);
  return parts.join(" ");
}

function SaveToNotesButton({
  savedNoteId,
  buildDraft,
  onSaveNote,
  onOpenNote,
}: {
  savedNoteId: number | null;
  buildDraft: () => NoteDraft;
  onSaveNote: (draft: NoteDraft) => Promise<number>;
  onOpenNote?: (noteId: number) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleClick() {
    if (savedNoteId != null) {
      onOpenNote?.(savedNoteId);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSaveNote(buildDraft());
    } catch (e: any) {
      setErr(e?.message ?? "Failed to save note");
    } finally {
      setSaving(false);
    }
  }

  const isSaved = savedNoteId != null;
  return (
    <span className="turn__save-wrap">
      {err && <span className="muted turn__save-err">{err}</span>}
      <button
        type="button"
        className={`btn btn--ghost btn--small turn__save-note${isSaved ? " turn__save-note--saved" : ""}`}
        onClick={handleClick}
        disabled={saving}
        title={
          isSaved
            ? "Saved to notes — click to open"
            : "Capture this Q&A as a note"
        }
        aria-pressed={isSaved}
      >
        {saving ? "Saving…" : isSaved ? "✓ Saved · Open" : "Save to notes"}
      </button>
    </span>
  );
}

function ProgressRow({ turn }: { turn: ChatTurn }) {
  const label =
    turn.status === "queued"
      ? turn.queue_position > 0
        ? `Queued (#${turn.queue_position + 1} in line)`
        : "Queued"
      : turn.progress_step ?? "Working";
  return (
    <div className="turn__progress">
      <div className="turn__progress-row">
        <span className="muted">{label}</span>
        <span className="muted">{turn.progress_pct}%</span>
      </div>
      <div className="progress">
        <div className="progress__bar" style={{ width: `${turn.progress_pct}%` }} />
      </div>
    </div>
  );
}

function AnswerText({
  text,
  citations,
  caseId,
}: {
  text: string;
  citations: Citation[];
  caseId: number;
}) {
  const index = useMemo(
    () => new Map(citations.map((c) => [c.provenance_token, c])),
    [citations],
  );
  const [openToken, setOpenToken] = useState<string | null>(null);

  // Stable token→reference-number map. We scan the text in display order
  // and assign each unique token an incrementing number, so the answer
  // body reads like a paper with footnotes — `[1]`, `[2]`, etc. — instead
  // of repeating the document name + page in every chip. Same token
  // appearing twice keeps the same number.
  const tokenOrder = useMemo(() => {
    const order = new Map<string, number>();
    let n = 0;
    const re = /\[PT-[^\]]+\]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const inner = m[0].slice(1, -1);
      for (const tok of inner.split(/\s*,\s*/)) {
        const t = tok.trim();
        if (/^PT-/.test(t) && !order.has(t)) order.set(t, ++n);
      }
    }
    return order;
  }, [text]);

  const openCitation = openToken ? index.get(openToken) ?? null : null;

  // The model is supposed to emit one token per bracket — `[PT-...] [PT-...]`
  // — but occasionally it groups several into a single bracket, e.g.
  // `[PT-D9-..., PT-D6-...]`. Split inside-bracket commas so each token
  // becomes its own clickable chip (otherwise a click on the comma-joined
  // string is treated as one unknown token and 404s).
  function renderChip(token: string, key: string) {
    const c = index.get(token);
    const num = tokenOrder.get(token);
    const label = num != null ? String(num) : "?";
    let tip: string;
    if (!c) {
      tip = `${token} — click to view source`;
    } else if (c.source_type === "video") {
      const what = c.modality === "image" ? "scene" : "clip";
      tip = `${c.document_name ?? "Video"} @ ${formatClock(c.at_seconds)} — click to play ${what}`;
    } else if (c.modality === "image") {
      tip = `${c.document_name ?? "Image"} — click to view image`;
    } else {
      tip = `${c.document_name ?? "Source"}${c.page ? ` p.${c.page}` : ""}${c.paragraph ? ` ¶${c.paragraph}` : ""} — click to view source`;
    }
    return (
      <button
        key={key}
        type="button"
        className="cite cite--ref"
        title={tip}
        aria-label={tip}
        onClick={() => setOpenToken(token)}
      >
        {label}
      </button>
    );
  }

  // Split the answer into paragraphs on blank lines, then within each
  // paragraph split by citation tokens AND treat single newlines as
  // line breaks. Without this, the model's paragraph structure was
  // collapsed into one giant block.
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n[ \t]*\n+/);

  function renderTextSegment(segment: string, key: string) {
    if (!segment) return null;
    const lines = segment.split("\n");
    return (
      <span key={key}>
        {lines.map((ln, li) => (
          <span key={li}>
            {li > 0 ? <br /> : null}
            {ln}
          </span>
        ))}
      </span>
    );
  }

  return (
    <>
      {paragraphs.map((para, pi) => {
        const parts = para.split(/(\[PT-[^\]]+\])/g);
        return (
          <p key={pi}>
            {parts.map((p, i) => {
              const m = p.match(/^\[(PT-[^\]]+)\]$/);
              if (!m) return renderTextSegment(p, `${pi}-${i}`);
              const tokens = m[1]
                .split(/\s*,\s*/)
                .map((t) => t.trim())
                .filter((t) => /^PT-/.test(t));
              if (tokens.length === 0) return renderTextSegment(p, `${pi}-${i}`);
              return (
                <span key={`${pi}-${i}`} className="cite-group">
                  {tokens.map((tok, j) => renderChip(tok, `${pi}-${i}-${j}`))}
                </span>
              );
            })}
          </p>
        );
      })}
      {openToken && (
        <CitationModal
          token={openToken}
          citation={openCitation}
          caseId={caseId}
          onClose={() => setOpenToken(null)}
        />
      )}
    </>
  );
}

interface ChunkResolution {
  provenance_token: string;
  document_id: number;
  document_name: string;
  mime_type?: string | null;
  modality?: string | null;
  page?: number | null;
  paragraph?: number | null;
  chunk_index?: number | null;
  text: string;
  document_deleted?: boolean;
}

function CitationModal({
  token,
  citation,
  caseId,
  onClose,
}: {
  token: string;
  citation: Citation | null;
  caseId: number;
  onClose: () => void;
}) {
  // Video citations open a clip player seeked to the cited moment — the same
  // experience as the Videos section — instead of resolving a document chunk.
  if (citation?.source_type === "video" && citation.video_id != null) {
    return (
      <VideoClipModal
        citation={citation}
        caseId={caseId}
        videoId={citation.video_id}
        onClose={onClose}
      />
    );
  }
  return (
    <DocumentCitationModal
      token={token}
      citation={citation}
      caseId={caseId}
      onClose={onClose}
    />
  );
}

function DocumentCitationModal({
  token,
  citation,
  caseId,
  onClose,
}: {
  token: string;
  citation: Citation | null;
  caseId: number;
  onClose: () => void;
}) {
  const [chunk, setChunk] = useState<ChunkResolution | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  const isImage =
    citation?.modality === "image" || chunk?.modality === "image";
  const imgDocId = chunk?.document_id ?? citation?.document_id ?? null;

  // For image citations, fetch the raw image bytes as a blob and show the
  // picture inline in the modal (the user asked to "view the image in a nice
  // modal"). Revoked on close / citation switch.
  useEffect(() => {
    if (!isImage || imgDocId == null) {
      setImgUrl(null);
      return;
    }
    let cancel = false;
    let url: string | null = null;
    (async () => {
      try {
        const blob = await api.blob(`/cases/${caseId}/documents/${imgDocId}/raw`);
        if (cancel) return;
        url = URL.createObjectURL(blob);
        setImgUrl(url);
      } catch {
        /* fall back to the text caption if the image can't be loaded */
      }
    })();
    return () => {
      cancel = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [caseId, imgDocId, isImage]);

  // Esc-to-close + body scroll lock while modal is open.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Pull the full chunk so the user can read more than the answer-time snippet.
  useEffect(() => {
    let cancel = false;
    setChunk(null);
    setErr(null);
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<ChunkResolution>(
          `/cases/${caseId}/chunks/by-token/${encodeURIComponent(token)}`,
        );
        if (!cancel) setChunk(data);
      } catch (e: any) {
        if (!cancel) setErr(e?.message ?? "Could not resolve provenance");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [caseId, token]);

  async function openSource() {
    const docId = chunk?.document_id ?? citation?.document_id;
    if (docId == null) return;
    setOpening(true);
    setErr(null);
    try {
      const blob = await api.blob(`/cases/${caseId}/documents/${docId}/raw`);
      const url = URL.createObjectURL(blob);
      const page = chunk?.page ?? citation?.page ?? null;
      const target = page ? `${url}#page=${page}` : url;
      window.open(target, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setErr(e?.message ?? "Could not open source file");
    } finally {
      setOpening(false);
    }
  }

  const doc = chunk?.document_name ?? citation?.document_name ?? "Source";
  const page = chunk?.page ?? citation?.page ?? null;
  const para = chunk?.paragraph ?? citation?.paragraph ?? null;
  const snippet = citation?.text ?? null;
  const fullText = chunk?.text ?? null;
  const isDeleted = chunk?.document_deleted === true;
  const showSnippetSeparately =
    !!snippet && (!fullText || !fullText.includes(snippet.trim()));

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="citation-modal-title"
      onClick={onClose}
    >
      <div
        className="modal__card modal__card--lg modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <div className="modal__header-meta">
              <span className="modal__header-date">
                {isImage
                  ? "Image"
                  : page != null
                    ? `Page ${page}`
                    : "Page —"}
                {!isImage && para != null ? ` · ¶${para}` : ""}
              </span>
            </div>
            <h2 id="citation-modal-title" className="modal__header-title">
              {doc}
            </h2>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        {isDeleted && (
          <div className="alert alert--warning modal__deleted-banner" role="note">
            This document has been removed from the case. The source text below is preserved for reference, but the original file can no longer be opened.
          </div>
        )}

        <div className="modal__body modal__body--flat">
          {isImage && imgUrl && (
            <div className="modal__section">
              <img
                src={imgUrl}
                alt={doc}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "55vh",
                  margin: "0 auto",
                  borderRadius: 8,
                  objectFit: "contain",
                }}
              />
            </div>
          )}

          {showSnippetSeparately && snippet && (
            <div className="modal__section">
              <div className="modal__section-label">Cited snippet</div>
              <blockquote className="timeline__quote">"{snippet}"</blockquote>
            </div>
          )}

          <div className="modal__section">
            <div className="modal__section-label">
              {isImage ? "AI description" : "Source paragraph"}
            </div>
            {loading && <div className="muted">Loading source…</div>}
            {err && !loading && <div className="alert alert--error">{err}</div>}
            {!loading && !err && fullText && (
              <pre className="timeline__prov-text">{fullText}</pre>
            )}
            {!loading && !err && !fullText && snippet && (
              <blockquote className="timeline__quote">"{snippet}"</blockquote>
            )}
          </div>

          <div className="modal__section">
            <div className="modal__section-label">Provenance</div>
            <div className="timeline__prov">
              <div className="timeline__prov-meta">
                <span className="timeline__prov-doc">{doc}</span>
                {page != null && <span className="muted">· page {page}</span>}
                {para != null && <span className="muted">· ¶{para}</span>}
                {citation?.score != null && (
                  <span className="muted">
                    · relevance {citation.score.toFixed(2)}
                  </span>
                )}
              </div>
              <div className="timeline__prov-token">{token}</div>
            </div>
          </div>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {doc}
            {page ? ` · p.${page}` : ""}
            {para ? ` · ¶${para}` : ""}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={openSource}
              disabled={
                isDeleted || opening || (chunk?.document_id == null && citation?.document_id == null)
              }
              title={isDeleted ? "Source document has been removed" : "Open the original source file in a new tab"}
            >
              {isDeleted
                ? "Document removed"
                : opening
                  ? "Opening…"
                  : isImage
                    ? "Open image"
                    : "Open source"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function VideoClipModal({
  citation,
  caseId,
  videoId,
  onClose,
}: {
  citation: Citation;
  caseId: number;
  videoId: number;
  onClose: () => void;
}) {
  const [video, setVideo] = useState<Video | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const at = citation.at_seconds ?? 0;

  // Esc-to-close + body scroll lock.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Resolve the video so we get a freshly-signed play_url.
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const v = await api.get<Video>(`/cases/${caseId}/videos/${videoId}`);
        if (!cancel) setVideo(v);
      } catch (e: any) {
        if (!cancel) setErr(e?.message ?? "Could not load video");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [caseId, videoId]);

  const playSrc = video?.play_url ? mediaSrc(video.play_url) : null;

  // Seek to the cited moment once metadata is ready, then try autoplay.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playSrc) return;
    const seekAndPlay = () => {
      try {
        v.currentTime = Math.max(0, at);
      } catch {
        /* range error if duration unknown — ignore */
      }
      v.play().catch(() => {
        /* autoplay may be blocked; user can press play */
      });
    };
    if (v.readyState >= 1) {
      seekAndPlay();
    } else {
      v.addEventListener("loadedmetadata", seekAndPlay, { once: true });
      return () => v.removeEventListener("loadedmetadata", seekAndPlay);
    }
  }, [playSrc, at]);

  const name = video?.original_name ?? citation.document_name ?? "Video";

  return (
    <div className="modal no-print" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal__card modal__card--lg modal__card--split"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <div className="modal__header-meta">
              <span className="modal__header-date">Clip @ {formatClock(at)}</span>
            </div>
            <h2 className="modal__header-title">{name}</h2>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body modal__body--flat">
          {loading && <div className="muted">Loading clip…</div>}
          {err && !loading && <div className="alert alert--error">{err}</div>}
          {playSrc && (
            <div className="modal__section">
              <video
                ref={videoRef}
                src={playSrc}
                controls
                preload="metadata"
                style={{
                  width: "100%",
                  maxHeight: "60vh",
                  borderRadius: 8,
                  background: "#000",
                }}
              />
            </div>
          )}
          {citation.text && (
            <div className="modal__section">
              <div className="modal__section-label">
                {citation.modality === "image" ? "Scene description" : "Transcript"}
              </div>
              <pre className="timeline__prov-text">{citation.text}</pre>
            </div>
          )}
          <div className="modal__section">
            <div className="modal__section-label">Provenance</div>
            <div className="timeline__prov">
              <div className="timeline__prov-meta">
                <span className="timeline__prov-doc">{name}</span>
                <span className="muted">· {formatClock(at)}</span>
                {citation.score != null && (
                  <span className="muted">· relevance {citation.score.toFixed(2)}</span>
                )}
              </div>
              <div className="timeline__prov-token">{citation.provenance_token}</div>
            </div>
          </div>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {name} · {formatClock(at)}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function MiniTimeline({
  events,
  citations,
  caseId,
  onSaved,
}: {
  events: TimelineEvent[];
  citations: Citation[];
  caseId: number;
  onSaved?: () => void;
}) {
  const idx = new Map(citations.map((c) => [c.provenance_token, c]));
  const sorted = [...events].sort((a, b) => {
    const ad = a.event_date ?? a.event_datetime ?? "";
    const bd = b.event_date ?? b.event_datetime ?? "";
    return ad.localeCompare(bd);
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ inserted: number; skipped: number } | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setSaveErr(null);
    try {
      const result = await api.post<{ inserted: number; skipped: number }>(
        `/cases/${caseId}/timeline`,
        { events: sorted },
      );
      setSaved(result);
      onSaved?.();
    } catch (e: any) {
      setSaveErr(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mini-timeline-wrap">
      <div className="mini-timeline-bar">
        <span className="muted">{sorted.length} events from this answer</span>
        {!saved ? (
          <button
            type="button"
            className="btn btn--small"
            onClick={save}
            disabled={saving}
            title="Add these events to the case Timeline tab"
          >
            {saving ? "Saving…" : "Save to case timeline"}
          </button>
        ) : (
          <span className="muted">
            Saved {saved.inserted} · {saved.skipped} duplicates
          </span>
        )}
      </div>
      {saveErr && <div className="alert alert--error">{saveErr}</div>}
      <ol className="mini-timeline">
        {sorted.map((e, i) => {
          const c = idx.get(e.provenance_token);
          return (
            <li key={i}>
              <time>{e.event_date ?? "—"}</time>
              <div>
                <div className="mini-timeline__head">{e.headline}</div>
                {e.description && <div className="muted">{e.description}</div>}
                {c && (
                  <div className="mini-timeline__src">
                    {c.source_type === "video"
                      ? `${c.document_name ?? "Video"} — ${formatClock(c.at_seconds)}`
                      : c.modality === "image"
                        ? `${c.document_name ?? "Image"}`
                        : `${c.document_name ?? "Source"}${c.page ? ` — p.${c.page}` : ""}${c.paragraph ? ` ¶${c.paragraph}` : ""}`}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
